import type { TrackingProps } from 'contexts/trackingContext'
import React, { useContext, useEffect, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import { getYMALViewForOffer } from 'components/Recommendations/YouMayAlsoLike/Shared/YouMayAlsoLikeOffersCarousel'
import GeoContext from 'contexts/geoContext'
import useOffer from 'hooks/Offers/useOffer'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import HighIntentRecommendationTileLoader from './HighIntentRecommendationTileLoader'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED, isOfferValidWithRegion } from 'lib/offer/highIntentOffers'
import useBedbankRates from 'hooks/useBedbankRates'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import config from 'constants/config'
import moment from 'moment'
import type { HighIntentTileProps } from 'components/OfferList/HighIntent/HighIntentRecommendationTile'
import HighIntentRecommendationTile from 'components/OfferList/HighIntent/HighIntentRecommendationTile'
import { HighIntentOfferDisplayStatus } from 'home/components/OfferCarousels/HighIntentOfferCard'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import { FlightsClassTypes } from 'constants/flight'
import { MALDIVES_CHARTER_END_TARGET_DATE, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { EmptyObject } from 'lib/object/objectUtils'

export function shouldDisplay(offerType: App.OfferType) {
  return ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offerType)
}

interface Props {
  offerId: string
  highIntentCategory: App.HighIntentCategory
  position: number
  tracking?: TrackingProps
  lereVersion?: string
  onClick?: HighIntentTileProps['onClick']
  onClose?: HighIntentTileProps['onClose']
  className?: string
  onDisplayStatusChange?: (status: HighIntentOfferDisplayStatus) => void
}

const tileTestId = 'recently-viewed-tile'
const DEFAULT_CHARTER_OCCUPANCIES = [config.search.defaultOccupants]

function RecentlyViewedTile(props: Props) {
  const { offerId, highIntentCategory, position, tracking, lereVersion, onClick, onClose, onDisplayStatusChange, className } = props
  const { currentCurrency } = useContext(GeoContext)
  const [offer, fetching, offerError] = useOffer<App.HotelOffer | App.HotelOfferSummary>(offerId)

  const defaultDepositAmountPercentage = useAppSelector(getDefaultDepositAmountPercentage)
  const defaultAirportCode = useAppSelector(getDefaultAirportCode)
  const charterBundleRoute = useAppSelector((state: App.State) => getCharterBundleRoute(state, offer))

  // These are for bedbanks, we try to get 1 night price in 2 weeks
  const { checkIn, checkOut } = useMemo(() => ({
    checkIn: moment().add(2, 'weeks').format('YYYY-MM-DD'),
    checkOut: moment().add(2, 'weeks').add(1, 'day').format('YYYY-MM-DD'),
  }
  ), [])
  const [{ hotelOnlyRates }, fetchingRates] = useBedbankRates(offerId, [config.search.defaultOccupants], checkIn, checkOut, isBedbankOffer(offer))
  const bedbankRate = hotelOnlyRates[0]

  const view = useMemo(() => {
    if (offer) {
      return getYMALViewForOffer(offer, {
        testId: tileTestId,
        position,
        currency: currentCurrency,
        tracking,
        defaultDepositAmountPercentage,
      })
    }
  }, [currentCurrency, defaultDepositAmountPercentage, offer, position, tracking])

  const {
    category,
    flights,
    image,
    title,
    pricing,
    shouldDisplayValue,
    testId,
    url,
    value,
    urgencyLabels,
  } = (view ?? {}) as any
  // ------- Render props -------------
  let { price, duration, memberPrice } = pricing ?? {}

  let displayedPrice
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)

  const charterBundle = !!charterBundleRoute
  const charterFilters = useMemo<App.OfferListFilters>(() => (
    charterBundle
      ? {
          checkIn: MALDIVES_CHARTER_START_TARGET_DATE,
          checkOut: MALDIVES_CHARTER_END_TARGET_DATE,
          rooms: DEFAULT_CHARTER_OCCUPANCIES,
        }
      : EmptyObject
  ), [charterBundle])
  const directSearchPrices = useDirectSearchPrices({ filters: charterFilters, offerId })
  const shouldFetchCharterFlightPrice = !!(charterBundle && charterBundleRoute)
  const [charterFlightPrice, charterFlightPriceLoading] = useCharterFlightPrice({
    charterBundleRoute: charterBundleRoute ?? 'melbourne-maldives',
    checkInDate: charterFilters.checkIn,
    checkOutDate: charterFilters.checkOut,
    occupancies: DEFAULT_CHARTER_OCCUPANCIES,
    fareClass: FlightsClassTypes.ECONOMY,
    disabled: !shouldFetchCharterFlightPrice,
  })

  const charterRoomMultiplier = DEFAULT_CHARTER_OCCUPANCIES.length || 1
  const charterHotelPrice = directSearchPrices?.lowestPrice ?? ((price ?? 0) * charterRoomMultiplier)
  const charterHotelMemberPrice = directSearchPrices?.lowestMemberPrice ?? ((memberPrice ?? 0) * charterRoomMultiplier)
  const charterHotelValue = directSearchPrices?.lowestPriceValue ?? ((value ?? price ?? 0) * charterRoomMultiplier)

  const baseHotelPrice = charterBundle ? charterHotelPrice : (price ?? 0)
  const baseHotelMemberPrice = charterBundle ? charterHotelMemberPrice : (memberPrice ?? 0)
  const baseHotelValue = charterBundle ? charterHotelValue : (value ?? baseHotelPrice)
  const baseDisplayedPrice = (canViewLuxPlusBenefits && baseHotelMemberPrice > 0 ? baseHotelMemberPrice : baseHotelPrice) ?? 0

  const shouldBundleWithFlights = charterBundle || !!offer?.bundledWithFlightsOnly
  const nonCharterFlightPrice = useMemo(() => {
    if (!shouldBundleWithFlights || !defaultAirportCode || !offer?.flightPrices || charterBundle) {
      return undefined
    }
    return offer.flightPrices[defaultAirportCode]
  }, [charterBundle, defaultAirportCode, offer, shouldBundleWithFlights])
  const flightPrice = charterBundle ? charterFlightPrice : nonCharterFlightPrice

  displayedPrice = baseDisplayedPrice + (flightPrice ?? 0)

  if (isBedbankOffer(offer) && !value) {
    displayedPrice = bedbankRate?.totals.inclusive + (bedbankRate?.totals.propertyFees ?? 0)
    // TODO: i18n - Category C: unit-coupled text "1 night", needs plural-aware ICU message
    duration = '1 night'
  }

  const displayValue = getHotelOfferDisplayedValue(
    !!offer?.bundledWithFlightsOnly,
    baseDisplayedPrice,
    baseHotelValue ?? 1,
    !!shouldDisplayValue,
    flightPrice,
  )
  const { shouldShowDiscountPercent, highestDiscountPercent } = displayValue
  const discountPercentage = (!offer?.isDiscountPillHidden && shouldShowDiscountPercent) ? highestDiscountPercent : undefined
  // TODO: i18n - Category C: unit-coupled duration text "${duration} from" / "From", needs plural-aware ICU message
  const durationText = duration ? `${duration} from` : 'From'

  const isValid = useMemo(() => offer ? isOfferValidWithRegion(offer) : false, [offer])

  const updatedTracking = useMemo(() => (tracking ? {
    ...tracking,
    category,
    lere: {
      version: lereVersion,
    },
  } : undefined), [tracking, category, lereVersion])

  const isLoading = fetching ||
    (isBedbankOffer(offer) && fetchingRates) ||
    (charterBundle && charterFlightPriceLoading)

  const shouldNotDisplay = offer?.type && !shouldDisplay(offer.type) || !!flights?.bundledWithFlightsOnly || !isValid || offerError

  useEffect(() => {
    if (isLoading) {
      onDisplayStatusChange?.(HighIntentOfferDisplayStatus.LOADING)
    } else if (shouldNotDisplay) {
      onDisplayStatusChange?.(HighIntentOfferDisplayStatus.HIDE)
    } else {
      onDisplayStatusChange?.(HighIntentOfferDisplayStatus.SHOW)
    }
  }, [isLoading, onDisplayStatusChange, shouldNotDisplay])

  if (isLoading) {
    return <HighIntentRecommendationTileLoader />
  }

  if (shouldNotDisplay) {
    return null
  }

  const tileProperties: HighIntentTileProps = {
    offerId,
    highIntentCategory,
    url,
    title,
    image,
    urgencyLabels,
    durationText,
    displayedPrice,
    isMemberPrice: memberPrice > 0,
    discountPercentage,
    position,
    tracking: updatedTracking,
    testId,
    onClick,
    onClose,
    className,
  }

  return (
    <HighIntentRecommendationTile {...tileProperties} />
  )
}

export default React.memo(RecentlyViewedTile)
